import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Simple UUID-ish generator (session-scoped, no external deps needed).
const genSessionId = () =>
  `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const ChatBot = () => {
  const { lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => genSessionId());
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState(() => [
    {
      role: "assistant",
      text:
        lang === "ta"
          ? "வணக்கம்! நான் Vetri Guide ஆதரவு உதவியாளர். Group 4, Group 2/2A, TNUSRB பற்றி எதுவும் கேளுங்கள்."
          : "Hi! I'm the Vetri Guide support assistant. Ask me anything about Group 4, Group 2/2A or TNUSRB prep.",
    },
  ]);
  const scrollRef = useRef(null);

  // Auto-scroll on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const suggestions =
    lang === "ta"
      ? [
          "Group 4 விலை என்ன?",
          "7-நாள் இலவசம் எப்படி?",
          "ரெஃபரல் நன்மைகள் என்ன?",
        ]
      : [
          "How much is Group 4?",
          "How does the free trial work?",
          "Tell me about referrals",
        ];

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setBusy(true);

    // Placeholder assistant bubble to fill as tokens stream in
    setMessages((m) => [...m, { role: "assistant", text: "" }]);

    try {
      const resp = await fetch(`${BACKEND_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });

      if (!resp.ok || !resp.body) {
        throw new Error(`HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by "\n\n"
        const parts = buffer.split("\n\n");
        buffer = parts.pop() ?? "";

        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const raw = line.slice(5).trim();
          if (raw === "[DONE]") continue;
          try {
            const data = JSON.parse(raw);
            if (data.delta) {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant") {
                  copy[copy.length - 1] = {
                    ...last,
                    text: last.text + data.delta,
                  };
                }
                return copy;
              });
            } else if (data.error) {
              throw new Error(data.error);
            }
          } catch {
            /* ignore malformed frame */
          }
        }
      }
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && !last.text) {
          copy[copy.length - 1] = {
            ...last,
            text:
              lang === "ta"
                ? "மன்னிக்கவும், தற்போது இணைக்க முடியவில்லை. tnpsctamilapp@gmail.com மூலம் தொடர்பு கொள்ளுங்கள்."
                : "Sorry, I couldn't reach the assistant right now. Please email tnpsctamilapp@gmail.com.",
          };
        }
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          data-testid="chatbot-open"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[9998] group inline-flex items-center gap-2 rounded-full bg-blue-950 text-white pl-4 pr-5 py-3 shadow-2xl shadow-blue-950/30 hover:bg-blue-900 hover:-translate-y-0.5 transition-all"
          aria-label="Open support chat"
        >
          <span className="relative inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 text-slate-900">
            <MessageCircle size={16} strokeWidth={2.5} />
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-blue-950" />
          </span>
          <span className="text-sm font-bold" style={{ fontFamily: "Outfit, Noto Sans Tamil, sans-serif" }}>
            {lang === "ta" ? "உதவி கேட்க" : "Chat with us"}
          </span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-5 right-5 z-[9998] w-[calc(100vw-2.5rem)] sm:w-[380px] h-[560px] max-h-[80vh] bg-white rounded-3xl shadow-2xl border border-blue-900/10 flex flex-col overflow-hidden"
          style={{ fontFamily: "Outfit, Noto Sans Tamil, sans-serif" }}
        >
          {/* Header */}
          <div className="relative bg-gradient-to-br from-blue-950 to-blue-900 text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="h-10 w-10 rounded-full bg-white p-0.5">
                  <img
                    src="/images/vetri-guide-logo.png"
                    alt="Vetri Guide"
                    className="h-full w-full object-contain"
                  />
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-blue-950" />
              </div>
              <div className="leading-tight">
                <p className="font-bold text-sm">Vetri Guide</p>
                <p className="text-[11px] text-blue-200">
                  {lang === "ta" ? "ஆதரவு உதவியாளர் · ஆன்லைனில்" : "Support assistant · Online"}
                </p>
              </div>
            </div>
            <button
              data-testid="chatbot-close"
              onClick={() => setOpen(false)}
              className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
              aria-label="Close chat"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="chatbot-messages"
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#fdfbf7]"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${
                  m.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-[14px] leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-blue-950 text-white rounded-br-md"
                      : "bg-white border border-blue-900/10 text-slate-800 rounded-bl-md shadow-sm"
                  }`}
                >
                  {m.text ||
                    (busy && i === messages.length - 1 ? (
                      <Loader2 size={14} className="animate-spin text-slate-400" />
                    ) : (
                      ""
                    ))}
                </div>
              </div>
            ))}

            {/* Suggestion chips (only when starting) */}
            {messages.length <= 1 && !busy && (
              <div className="pt-2 flex flex-wrap gap-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    data-testid={`chatbot-suggest-${s.slice(0, 10)}`}
                    onClick={() => send(s)}
                    className="text-[12px] font-semibold px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-300/60 transition"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-blue-900/10 bg-white px-3 py-3 flex items-end gap-2">
            <textarea
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              placeholder={
                lang === "ta"
                  ? "உங்கள் கேள்வியை இங்கே தட்டச்சு செய்யுங்கள்..."
                  : "Type your question..."
              }
              className="flex-1 resize-none rounded-2xl border border-blue-900/15 bg-[#fdfbf7] px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 max-h-24"
            />
            <button
              data-testid="chatbot-send"
              onClick={() => send()}
              disabled={busy || !input.trim()}
              className="h-11 w-11 rounded-2xl bg-blue-950 text-white hover:bg-blue-900 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center transition shrink-0"
              aria-label="Send"
            >
              {busy ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Send size={16} />
              )}
            </button>
          </div>

          {/* Footer note */}
          <div className="px-4 pb-2.5 text-[10px] text-slate-400 text-center bg-white">
            {lang === "ta"
              ? "AI-அடிப்படையிலான உதவி · உண்மையான உதவிக்கு "
              : "AI-powered support · For human help "}
            <a
              href="mailto:tnpsctamilapp@gmail.com"
              className="text-blue-900 font-semibold hover:underline"
            >
              tnpsctamilapp@gmail.com
            </a>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
